import { useMutation, useQuery } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import { Button, Space, UploadFile } from 'antd';
import React, { useMemo, useState } from 'react';
import { toISODateFormat } from '@common/services/datetime';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  DISPUTE_PREVIEW_INFO_QUERY,
  DisputeReviewQueryResponse,
  PerformDisputeAction,
} from '@pay-bill/graphql';
import { AutoAnimatedHeight } from 'works/legacy/components/auto-animated-height';
import {
  DisputeBodyDetails,
  DisputeCommentInput,
  DisputeDiffLine,
  DisputeDiffSection,
  DisputeFilesInput,
  DisputeFilesSection,
  DisputeValuesForm,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DuplicatedDisputeAlert,
  InvoiceItemDiffLine,
  SimpleDiffLine,
} from '@pay-bill/components';
import {
  useDisputeDeletedFiles,
  useDisputeRefetchs,
  useHasValidEditDisputeFields,
} from '@pay-bill/hooks';
import { catchErrorWith, rejectSimpleErrors } from '@pay-bill/services';
import debounce from 'lodash/debounce';
import { HistoryDropDown, Form, Col, Row, Divider } from '@common/components';
type FormValues = {
  comment?: string;
  files?: UploadFile[];
  newHours?: number;
  billingAmount?: number;
  newPayCode?: string;
  finalRateCents?: number;
};

export const DisputeEditDrawerBody = ({
  dispute,
  goBack,
}: {
  dispute: DisputeReviewQueryResponse['dispute'];
  goBack: () => void;
}) => {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;

  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<FormValues>(() => ({
    finalRateCents: 0,
    files: [],
  }));
  const [removedFiles, onFileDelete, maxFilesSlots] = useDisputeDeletedFiles(
    entityType === 'Organization'
      ? dispute.files.organization
      : dispute.files.supplier
  );

  const [performAction, { loading: loadingPerform }] =
    useMutation(PerformDisputeAction);

  const renderTitle = () => {
    if (!dispute) {
      return null;
    }

    const type = dispute.disputeType === 'error' ? 'Error' : 'Missing Line';

    return `Edit ${type} Dispute`;
  };

  const previewVariables = useMemo(() => {
    if (!formValues.newPayCode) {
      return null;
    }

    return {
      skipRates: dispute.canChangeBillingAmount.value,
      newHours: formValues.newHours || 0,
      newDate: toISODateFormat(dispute.newDate),
      newPayCode: formValues.newPayCode,
      finalRateCents: formValues.finalRateCents
        ? Math.floor(formValues.finalRateCents * 100)
        : undefined,
      staffingRequestAssignmentId: dispute.staffingRequestAssignment.id,
    };
  }, [
    formValues.newPayCode,
    formValues.newHours,
    formValues.finalRateCents,
    dispute.canChangeBillingAmount.value,
    dispute.newDate,
    dispute.staffingRequestAssignment.id,
  ]);

  const { data: previewData, loading: loadingPreview } = useQuery(
    DISPUTE_PREVIEW_INFO_QUERY,
    {
      skip: !previewVariables,
      variables: previewVariables!,
    }
  );

  const { previewRate, disputeMatches } = previewData || {};

  const formInvalid = !useHasValidEditDisputeFields(form, formValues);
  const hasDuplication = useMemo(() => {
    if (disputeMatches?.dispute) {
      return disputeMatches.dispute.id !== dispute.id;
    }

    return !!disputeMatches?.invoiceItem;
  }, [dispute.id, disputeMatches?.dispute, disputeMatches?.invoiceItem]);
  const invoicePreCalculated = dispute.worksInvoice.preCalculated;

  const calculatedRate = previewRate?.finalRateCents || 0;
  const calculatedBillingAmount = previewRate?.billingAmountCents || 0;

  const refetchQueries = useDisputeRefetchs();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = (values: Record<string, any>) => {
    if (loadingPerform) {
      return;
    }

    performAction({
      variables: {
        disputeId: dispute.id,
        input: {
          action: 'edit',
          newHours: values.newHours,
          newPayCode: values.newPayCode,
          finalRateCents: values.finalRateCents * 100,
          billingAmountCents: values.billingAmount * 100,
          comment: values.comment,
          addedFiles: values.files || [],
          removedFiles: removedFiles.map((file) => file.id),
        },
      },
    })
      .then(rejectSimpleErrors('performDisputeAction'))
      .then(() => {
        createMessage('success', 'Dispute edited');
        goBack();
        refetchQueries();
      })
      .catch(catchErrorWith('Failed to edit dispute. Please try again.'));
  };

  function onResetBillingRate() {
    setFormValues({
      ...previewVariables,
      finalRateCents: undefined,
    });
  }

  const onFormChange = debounce(
    (changes: FormValues, formValues: FormValues) => {
      const updatedValues = {
        ...formValues,
        ...changes,
      };

      if (changes.newPayCode) {
        updatedValues.finalRateCents = undefined;
      }

      if (Array.isArray(updatedValues.newHours)) {
        updatedValues.newHours = updatedValues.newHours[1];
      }

      setFormValues(updatedValues);
    },
    500
  );

  return (
    <>
      <DrawerTitle>{renderTitle()}</DrawerTitle>
      <DrawerBody>
        <DisputeBodyDetails dispute={dispute} showStatus={false} />
        <Form
          form={form}
          initialValues={formValues}
          onFinish={onFinish}
          onValuesChange={onFormChange}
        >
          <DisputeDiffSection mode="form">
            {dispute.disputeType === 'error' && dispute.worksInvoiceItem ? (
              <InvoiceItemDiffLine item={dispute.worksInvoiceItem} />
            ) : (
              <SimpleDiffLine.Empty />
            )}
            <DisputeDiffLine dispute={dispute} />
            <DisputeValuesForm
              form={form}
              payCodes={dispute.organization.payCodes}
              billingRate={calculatedRate}
              billingAmountCents={calculatedBillingAmount}
              canChangeBillingAmount={dispute.canChangeBillingAmount.value}
              showEstimationSymbol={invoicePreCalculated}
              onResetBillingRate={onResetBillingRate}
            />
            {invoicePreCalculated && entityType === 'Supplier' && (
              <p>
                * Billing Amount is an estimate and may differ from the final
                amount.
              </p>
            )}
            <AutoAnimatedHeight visible={hasDuplication}>
              <div style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <DuplicatedDisputeAlert
                      dispute={disputeMatches?.dispute}
                      invoiceItem={disputeMatches?.invoiceItem}
                    />
                  </Col>
                </Row>
              </div>
            </AutoAnimatedHeight>
          </DisputeDiffSection>
          <DisputeCommentInput />
          <Row className="dispute-upload-row">
            <Col span={24}>
              <DisputeFilesInput maxFilesSlots={maxFilesSlots} />
            </Col>
          </Row>
          <Divider gutter={16} />
          <Row>
            <DisputeFilesSection
              dispute={dispute}
              canDeleteFiles={entityType}
              onDelete={onFileDelete}
              hiddenFiles={removedFiles}
            />
          </Row>
        </Form>
        <Divider gutter={16} />
        <HistoryDropDown data={dispute.history} hrVisible={false} />
      </DrawerBody>
      <DrawerFooter>
        <Space>
          <Col span={9}>
            <Button
              className="dispute-edit-button"
              type="default"
              size="large"
              onClick={goBack}
            >
              Back
            </Button>
          </Col>
          <Col span={9}>
            <Button
              className="dispute-edit-button"
              type="primary"
              size="large"
              onClick={form.submit}
              disabled={
                loadingPerform ||
                loadingPreview ||
                hasDuplication ||
                formInvalid
              }
            >
              Send Edits
            </Button>
          </Col>
        </Space>
      </DrawerFooter>
      <style jsx>
        {`
          :global(.select-option div) {
            white-space: break-spaces;
            word-break: break-word;
          }
          :global(.dispute-upload-row) {
            margin-top: 8px;
            max-width: 100%;
          }
        `}
      </style>
    </>
  );
};
